import { forwardRef, type HTMLAttributes, type Ref } from 'react';

import { cn } from '@/lib/utils';

import { Blueprint, type BlueprintElement } from './Blueprint';

/**
 * Card do Design System (ADR-0065): por default é um **objeto de blueprint** —
 * desenho de linha transparente, hairline e cantos retos. É o que as pranchas
 * mostram (KPIs, card do portal, tabela) e a
 * decisão do owner de 2026-07-28: o default carrega a linguagem, e quem precisa
 * de um bloco opaco pede `variant="plain"`.
 *
 * O único objeto sólido da tela é o botão primário — por isso nem `blueprint`
 * nem `plain` levam sombra.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'blueprint' | 'plain';
  /** Tag da moldura, quando o card é uma seção/figura semântica. */
  as?: BlueprintElement;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant = 'blueprint', as, ...props },
  ref,
) {
  if (variant === 'plain') {
    const Comp = (as ?? 'div') as 'div';
    return (
      <Comp
        ref={ref}
        className={cn('border border-border bg-surface text-fg', className)}
        {...props}
      />
    );
  }

  return (
    <Blueprint
      ref={ref as Ref<HTMLElement>}
      {...(as ? { as } : {})}
      className={cn('text-fg', className)}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div ref={ref} className={cn('flex flex-col gap-1.5 p-sc-6 pb-sc-3', className)} {...props} />
    );
  },
);

/**
 * Kicker do card: rótulo mono em caixa alta acima do título, como nas pranchas
 * ("CASOS NO MÊS", "ACESSO RESTRITO"). Aço a partir do 700 — o aço base não
 * alcança 4,5:1 como texto (ADR-0065 §9).
 */
export const CardKicker = forwardRef<HTMLSpanElement, HTMLAttributes<HTMLSpanElement>>(
  function CardKicker({ className, ...props }, ref) {
    return (
      <span
        ref={ref}
        className={cn(
          'font-mono text-[10px] uppercase leading-none tracking-[0.1em] text-accent-700',
          className,
        )}
        {...props}
      />
    );
  },
);

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, children, ...props }, ref) {
    return (
      <h3 ref={ref} className={cn('text-heading-sm', className)} {...props}>
        {children}
      </h3>
    );
  },
);

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return <p ref={ref} className={cn('text-body-sm text-fg-muted', className)} {...props} />;
});

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn('p-sc-6 pt-0', className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-end gap-2 border-t border-border p-sc-4',
          className,
        )}
        {...props}
      />
    );
  },
);
